import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../CDN';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import BenefitsCards, { BenefitsCardsPageSectionWrapper } from '../widgets/cards/BenefitsCards';
import FAQWidget from '../widgets/interactive/FAQWidget';

type XenceLandingPageStaticProps = {};

export const getStaticProps: GetStaticProps<XenceLandingPageStaticProps> = async (context) => {
  const props: XenceLandingPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const XenceLandingPage: NextPageWithLayout<XenceLandingPageStaticProps> = (props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.LandingPageXenceGuide);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Xence AI - Installation Guide"
        title="Instantly answer questions on Jira SM"
        description="With Xence AI you can answer employee questions automatically and instantly without getting involved. Employees get human-like answers straight from Confluence just by asking our Jira chatbot on the JSM support portal."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <BenefitsCardsPageSectionWrapper>
        <BenefitsCards
          records={[
            {
              imageUrl: CDNAssets.getUrl('benefit-fast-resolution'),
              title: 'Instant answers, zero delays',
              description: 'Employees get answers immediately on the JSM support portal.',
            },
            {
              imageUrl: CDNAssets.getUrl('benefit-insights'),
              title: 'Reliable insights and responses',
              description: "All answers are based on your company's Confluence knowledge base.",
            },
            {
              imageUrl: CDNAssets.getUrl('benefit-human-like-answers'),
              title: '24/7 unlimited accessibility',
              description: 'Our exclusive Jira chatbot is always available.',
            },
          ]}
        />
      </BenefitsCardsPageSectionWrapper>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Step by step installation" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-1'),
              orientation: 'left',
              title: '1. Find the Xence Helper app by Gaspar AI',
              description:
                'Simply search “Xence Helper by Gaspar AI” in Atlassian’s Marketplace. Xence Helper is responsible for reading your Confluence articles!',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-2'),
              orientation: 'right',
              title: '2. Click on "Get it now"',
              description: 'Review the app’s functions, then click on "Get it now" to install it in your Confluence instance.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-3'),
              orientation: 'left',
              title: '3. Find the Xence app by Gaspar AI',
              description: 'Simply search “Xence by Gaspar AI” in Atlassian’s Marketplace. Then click on “Try it free” button.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-4'),
              orientation: 'right',
              title: '4. Click on "Start free trial"',
              description: 'Review the app’s functions, then click on "Start free trial" to install it in your JSM instance.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-5'),
              orientation: 'left',
              title: '5. Login to your Atlassian Instance',
              description: 'You must log in with your existing account or create a new account on the spot!',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-6'),
              orientation: 'right',
              title: '6. Install in any JSM site',
              description:
                'If you have multiple sites, select which one to install onto. Note: The App can only read and respond from knowledge that is in a single site.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-7'),
              orientation: 'left',
              title: '7. Verify that you have installed the apps',
              description: 'By going to the “Manage apps” panel, verify that you have both apps installed before you start.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-8'),
              orientation: 'right',
              title: '8. Find your Portal link',
              description:
                'By going to the Portal settings page, you can find your portal link that you are going to send to your employees.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-9'),
              orientation: 'left',
              title: '9. Start chatting!',
              description:
                'Employees can now enjoy human-like answers to their questions, and you can get rid of routine, repetitive tasks!',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-xence-ai-installation-step-10'),
              orientation: 'right',
              title: '10. If Gaspar doesn’t answer the first time, just give them a moment!',
              description:
                'In some cases, if your knowledge base has many articles there may be a 5-minute delay, but don’t worry, it’s just the synchronisation process.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
XenceLandingPage.displayName = 'XenceLandingPage';
export default XenceLandingPage;
